import type {
  ColDef,
  ColGroupDef,
  Column,
  ColumnMovedEvent,
  ColumnState,
  EditableCallbackParams,
  RowHeightParams,
} from "ag-grid-community";
import { compareAsc } from "date-fns";
import { flatten } from "ramda";

import type { InputTableQuerySpec, ProjectFile } from "../graphql.js";
import {
  getActiveExpansionPath,
  getExpandedColumnsByKey,
  type ITableColumn,
  type ITableData,
} from "../redux/modules/table-data.js";
import { CELL_EXPANSION, CELL_TYPE, ROW_HEIGHT, SUBROW_HEIGHT } from "../settings/settings.js";
import { getCellEditor, getCellEditorParams } from "../ui-modules/core/grid/cell-editor-factory.js";
import { getCellRenderer } from "../ui-modules/core/grid/cell-renderer-factory.js";
import { getColumnProps, traverseAndCollect } from "../ui-modules/core/grid/column-props-factory.js";
import type { IColumnDef, ICustomColDef } from "../ui-modules/core/grid/DataTable.js";
import type { IGridContextProps } from "../ui-modules/core/grid/InMemoryTableWithSelectionModel.js";
import { parse } from "./rsql.js";
import { compareAlphaNumerically } from "./sort.js";
import type { IStringAnyMap } from "./type.js";

export type GridComparator = ColDef["comparator"];

/**
 * Return null if A and B have a value or one of -1,0,+1 if one or both are undefined or null.
 * It mimics cmp(A,B).
 * @return  null, -1, 0, +1
 */
function scalarNullCompare(valueA: any, valueB: any, isInverted: boolean): number | null {
  if (valueA == undefined && valueB == undefined) {
    return 0;
  }
  // The handling of isInverted is here because the null values should always rank lower than the real values.
  // This compensates for ag-grids handling of the inversion!
  // nulls/undefined always last
  if (valueA == undefined) {
    return isInverted ? -1 : 1;
  }

  if (valueB == undefined) {
    return isInverted ? 1 : -1;
  }

  return null;
}

/**
 * Return null if A and B contain values or one of -1,0,+1 if one or both are empty
 * It mimics cmp(A,B).
 * Caveat: A and B must not be undefined or null!
 * @return  null, -1, 0, +1
 */
function arrayNullCompare(valueA: any[] | null, valueB: any[] | null, isInverted: boolean): number | null {
  if (!valueA?.length && !valueB?.length) {
    return 0;
  }

  // The handling of isInverted is here because the null values should always rank lower than the real values.
  // This compensates for ag-grids handling of the invertion!
  // nulls always last
  if (!valueA?.length) {
    return isInverted ? -1 : 1;
  }

  if (!valueB?.length) {
    return isInverted ? 1 : -1;
  }

  return null;
}

/*
 * Beware:  ag-grid handles the isInverted flag to the result of the comparator, too!
 * We must handle it or we cancel out the effect!
 * FIXME:  What does that mean?
 *    *  these functions apply isInverted in null comparison only
 *    *  the other parts do not apply it (at least the local standard comparators do not)
 *     Does 'must handle it' mean 'ignore it' in real comparisons??  Why apply it in  scalarNullCompare then?
 */

function stringComparator(valueA: string, valueB: string, _nodeA, _nodeB, isInverted: boolean): number {
  const scalarNullResult = scalarNullCompare(valueA, valueB, isInverted);
  return scalarNullResult === null ? valueA.toUpperCase().localeCompare(valueB.toUpperCase()) : scalarNullResult;
}

function numberComparator(valueA: number, valueB: number, _nodeA, _nodeB, isInverted: boolean): number {
  const scalarNullResult = scalarNullCompare(valueA, valueB, isInverted);
  return scalarNullResult === null ? valueA - valueB : scalarNullResult;
}

function booleanComparator(valueA, valueB, _nodeA, _nodeB, isInverted: boolean): number {
  const scalarNullResult = scalarNullCompare(valueA, valueB, isInverted);
  return scalarNullResult === null ? valueB - valueA : scalarNullResult;
}

function dateComparator(valueA: string, valueB: string, nodeA, nodeB, isDescending: boolean): number {
  return genericDateTimeComparator(valueA, valueB, nodeA, nodeB, isDescending, false);
}

function dateTimeComparator(valueA: string, valueB: string, nodeA, nodeB, isDescending: boolean): number {
  return genericDateTimeComparator(valueA, valueB, nodeA, nodeB, isDescending, true);
}

function fileNameComparator(valueA: string, valueB: string, _nodeA, _nodeB, isInverted: boolean): number {
  const scalarNullResult = scalarNullCompare(valueA, valueB, isInverted);
  return scalarNullResult === null ? compareAlphaNumerically(valueA, valueB, false, true) : scalarNullResult;
}

function genericDateTimeComparator(
  valueA: any,
  valueB: any,
  _nodeA,
  _nodeB,
  isDescending: boolean,
  _useTime: boolean,
): number {
  // replace "" by `null`
  const valA = (Array.isArray(valueA) ? valueA[0] : valueA) || null;
  const valB = (Array.isArray(valueB) ? valueB[0] : valueB) || null;

  const scalarNullResult = scalarNullCompare(valA, valB, isDescending);

  if (scalarNullResult !== null) {
    return scalarNullResult;
  }

  const dateA = new Date(valA);
  const dateB = new Date(valB);

  // We don't have to invert the test regarding what isDescending is, ag-grid takes care of it.
  return compareAsc(dateA, dateB);
}

export interface INamedEntity {
  name: string;
}

function fileListComparator(
  valueA: ProjectFile[] | null,
  valueB: ProjectFile[] | null,
  _nodeA,
  _nodeB,
  isInverted: boolean,
): number {
  // Keeping isInverted allows to always keep empty cells at the end.
  const arrayNullResult: number = arrayNullCompare(valueA, valueB, isInverted);

  if (arrayNullResult !== null) {
    // consider valid "0" outcome!
    return arrayNullResult;
  }

  // concatenate the values from the array before comparing,
  const valueAConcat = valueA?.map((p) => p.fileName).join() ?? "";
  const valueBConcat = valueB?.map((p) => p.fileName).join() ?? "";

  // Don't take isInverted into account for comparator. Ag-grid does it itself.
  // This result is only returned if both cells have values.
  return valueAConcat[0].localeCompare(valueBConcat);
}

function stringListComparator(
  valueA: (string | INamedEntity)[] | null,
  valueB: (string | INamedEntity)[] | null,
  _nodeA,
  _nodeB,
  isInverted: boolean,
): number {
  // Keeping isInverted allows to always keep empty cells at the end.
  const arrayNullResult: number = arrayNullCompare(valueA, valueB, isInverted);

  if (arrayNullResult !== null) {
    // consider valid "0" outcome!
    return arrayNullResult;
  }

  // concatenate the values from the array before comparing,
  const valueAConcat = flatten(valueA ?? []).join();
  const valueBConcat = flatten(valueB ?? []).join();

  // Don't take isInverted into account for comparator. Ag-grid does it itself.
  // This result is only returned if both cells have values.
  return valueAConcat.localeCompare(valueBConcat);
}

function numberListComparator(valueA: number[], valueB: number[], _nodeA, _nodeB, isInverted: boolean): number {
  const arrayNullResult: number = arrayNullCompare(valueA, valueB, isInverted);

  return arrayNullResult === null ? valueA[0] - valueB[0] : arrayNullResult;
}

export function getComparatorByCellType(cellType: string): GridComparator {
  switch (cellType) {
    case CELL_TYPE.STRING_LIST:
      return stringListComparator;
    case CELL_TYPE.NUMBER:
    case CELL_TYPE.INTEGER:
      return numberComparator;
    case CELL_TYPE.NUMBER_LIST:
      return numberListComparator;
    case CELL_TYPE.BOOLEAN:
      return booleanComparator;
    case CELL_TYPE.DATE:
      return dateComparator;
    case CELL_TYPE.DATE_TIME:
      return dateTimeComparator;
    case CELL_TYPE.FILENAME:
      return fileNameComparator;
    case CELL_TYPE.FILE:
      return fileListComparator;
    default:
      return stringComparator;
  }
}

interface ICreateColumnDefProps {
  column: Partial<ITableColumn>;
  tableQuerySpec: Partial<InputTableQuerySpec>;
  cellRendererParamsLookup?: IStringAnyMap;
  supportClientSorting?: boolean;
  openLinkExternal?: boolean;
  wrapColumnHeaders?: boolean;
  gridContext?: IGridContextProps;
}

export function createColumnDef({
  column,
  tableQuerySpec,
  cellRendererParamsLookup,
  supportClientSorting,
  openLinkExternal,
  wrapColumnHeaders,
  gridContext,
}: ICreateColumnDefProps): ICustomColDef {
  let comparator: GridComparator = null;

  // assign a comparator function if the table is in memory (no server side sorting)
  if (supportClientSorting) {
    comparator = getComparatorByCellType(column.cellType);
  }
  // Base props
  let columnDef: ICustomColDef = {
    headerName: column.name,
    field: column.field,
    type: column.cellType,
    // when determining the colId retrieval, column.editable needs to be checked to work correctly with the
    // non-editable columns like organism name in phenotype editing
    colId: gridContext?.editMode && column.editable ? String(column.columnId) : column.field,
    pinned: column.fixed,
    suppressMovable: column.fixed,
    suppressHeaderMenuButton: true,
    sortable: column.sortable,
    minWidth: 100,
    filterable: column.filterable,
    cellClass: `data-cell`,
    attribute: column.attribute,
    comparator: column.comparator || comparator,
    singleClickEdit: false,
    editable: getEditableCallback(!!column.editable),
    openLinkExternal: openLinkExternal,
    cellExpansion: column.cellExpansion,
    groupField: column.groupField,
    multiValuesContext: column.multiValuesContext,
    autoHeight: column.autoHeight,
    searchAttributes: column.searchAttributes,
    cellEditor: column.editable ? getCellEditor(column) : null,
    cellEditorParams: column.editable ? getCellEditorParams(column) : null,
    sortingOrder: column.sortingOrder,
    rowGroup: column.rowGroup,
    suppressPaste: column.suppressPaste,
    suppressFillHandle: column.suppressFillHandle,
  };

  if (column.valueSetter) {
    columnDef.valueSetter = column.valueSetter; // not sure why undefined does not work for getting the fallback from defaultColDef
  }
  if (column.valueGetter) {
    columnDef.valueGetter = column.valueGetter; // not sure why undefined does not work for getting the fallback from defaultColDef
  }

  // Sort direction
  if (column.sort) {
    columnDef.sort = column.sort.direction; //.toLowerCase(); does a good job but it triggers server side rows loading twice ?? // ag-grid OOFB sorting
    columnDef.sorting = column.sort;
  }

  // Filter value
  // TODO still needed ?
  if (column.filterExpression) {
    columnDef.filter = parse(column.filterExpression);
  }

  // Apply column-specific props
  const props: ColDef = getColumnProps(column);

  if (props) {
    columnDef = { ...columnDef, ...props };
  }

  // Get dynamic cell renderer
  columnDef.cellRenderer = getCellRenderer(column, tableQuerySpec, gridContext?.editMode);
  columnDef.cellRendererParams = cellRendererParamsLookup?.[column.field];
  columnDef.headerComponentParams = { wrapColumnHeaders: wrapColumnHeaders };
  return columnDef;
}

export function getEditableCallback(editable: boolean) {
  return (params: EditableCallbackParams) => {
    // params are used for dynamic row locking,
    // while editable is provided from the column and is the same for all rows in the column
    return (
      !params.data.locked && // is sample completely locked?
      !(params.data.editing && params.colDef.colId && params.data.editing[params.colDef.colId]?.readOnly) && // is cell read-only (because of edit once)?
      editable // is column editable at all?
    );
  };
}

interface ICreateColumnGroupsProps {
  tableColumns: Partial<ITableColumn>[];
  tableQuerySpec: Partial<InputTableQuerySpec>;
  createCheckboxColumnDef?: any;
  createRowDraggingColumnDef?: any;
  createLockedColumnDef?: any;
  cellRendererParamsLookup?: IStringAnyMap;
  supportClientSorting?: boolean;
  openLinkExternal?: boolean;
  wrapColumnHeaders?: boolean;
  gridContext?: IGridContextProps;
}

export function createColumnGroups({
  tableColumns,
  tableQuerySpec,
  createCheckboxColumnDef,
  createRowDraggingColumnDef,
  createLockedColumnDef,
  cellRendererParamsLookup,
  supportClientSorting,
  openLinkExternal,
  wrapColumnHeaders,
  gridContext,
}: ICreateColumnGroupsProps): IColumnDef[] {
  const columnGroups: IColumnDef[] = [];

  if (!tableColumns || tableColumns.length === 0) {
    return columnGroups;
  }

  // Add row dragging column
  if (createRowDraggingColumnDef) {
    columnGroups.push(createRowDraggingColumnDef(tableColumns[0].field));
  }

  // Add checkbox column
  if (createCheckboxColumnDef) {
    columnGroups.push(createCheckboxColumnDef());
  }

  // Add locked column
  if (createLockedColumnDef) {
    columnGroups.push(createLockedColumnDef());
  }

  // for separating the column groups with different styling
  let lastGroupColumn: ICustomColDef = null;
  let oddEvenColumnGroupSeparator = false;

  for (let columnIndex = 0; columnIndex < tableColumns.length; ) {
    const childColumns: IColumnDef[] = [];
    let groupName: string = null;
    let groupFixed = false;

    for (; columnIndex < tableColumns.length; columnIndex++) {
      const column: Partial<ITableColumn> = tableColumns[columnIndex];

      if (!column.hidden || column.fixed) {
        const colGroupName = !column.groupName ? "" : column.groupName;

        if (groupName === null) {
          groupName = colGroupName;
          groupFixed = column.fixed;
        } else if (!column.groupName || groupName !== colGroupName || groupFixed !== column.fixed) {
          break;
        }

        childColumns.push(
          createColumnDef({
            column,
            tableQuerySpec,
            cellRendererParamsLookup,
            supportClientSorting,
            openLinkExternal,
            wrapColumnHeaders,
            gridContext,
          }),
        );
      }
    }

    if (groupName !== null) {
      // we add some styling to the grouped columns to visually distinguish them [SEL7-352]
      if (groupName !== "" && !oddEvenColumnGroupSeparator) {
        // style the children by color and add a border to the last column
        childColumns.forEach((col: ICustomColDef) => (col.cellClass = col.cellClass + " column-group-even-color"));
        const lastColumn: ICustomColDef = childColumns[childColumns.length - 1] as ICustomColDef;
        lastColumn.cellClass = lastColumn.cellClass + " split-by-column-group-separator";
        // the previous column needs to the same separator (right border to be aligned with the header styling)
        if (lastGroupColumn) {
          lastGroupColumn.cellClass = lastGroupColumn.cellClass + " split-by-column-group-separator";
        }
        oddEvenColumnGroupSeparator = true;
      } else {
        oddEvenColumnGroupSeparator = false;
      }

      // adding set of grouped columns
      const columnGroup: ColGroupDef = {
        headerName: groupName,
        children: childColumns,
      };
      columnGroups.push(columnGroup);
    } else if (childColumns.length === 1) {
      // adding a single ungrouped column
      columnGroups.push(childColumns[0]);
    }
    lastGroupColumn = childColumns[childColumns.length - 1];
  }

  return columnGroups;
}

export function preventColumnPinning(params: ColumnMovedEvent, fixedColCount: number) {
  const columnState: ColumnState[] = params.api.getColumnState();
  const offset = fixedColCount - 1;

  // Make sure that no other columns are moved in front of the pinned ones.
  // Multiple columns might be moved via grouping
  params.columns.map((column: Column) => {
    const movedField: string = column.getColDef().colId;
    // ag-grid currently has a bug in params.toIndex, reporting 0 just after the last pinned column
    // (which should be 2). Therefore we have to determine the current index of the moved column
    // manually from the column state (via field).
    // We re-locate all actions to the left of index 2 (which actually also includes index 2
    // currently because of that bug, but this does not hurt).
    if (params.toIndex < fixedColCount) {
      const movedIdx: number = columnState.findIndex((state: ColumnState) => state.colId === movedField);

      // Move field to left-most allowed index:
      // This is 1 plus the number of moved columns (1 for a single col, but n for entire column groups)
      params.api.moveColumnByIndex(movedIdx, offset + params.columns.length);
    }
  });
}

export const calculateRowHeightForExpandedColumns = (tableData: ITableData, params: RowHeightParams): number => {
  if (!params.data) {
    return null;
  }
  let rowHeight: number = ROW_HEIGHT;
  const expansionPath = getActiveExpansionPath(tableData);

  expansionPath.forEach((expansionKey) => {
    const columns = getExpandedColumnsByKey(tableData, expansionKey);
    // while a column is expanded, all columns (except name) can be switched off, so columns === []
    if (columns.length > 0) {
      const cellExpansionMethod = columns?.[0].cellExpansion;

      switch (cellExpansionMethod) {
        case CELL_EXPANSION.STANDALONE:
        case CELL_EXPANSION.GROUP:
          {
            // we assume all of the expended columns need the same height since they are of the same expansion group ;
            // the expanded data needs to target an array
            const expandedColumnData: any[] = traverseAndCollect(params.data, expansionKey, false);
            rowHeight = Math.max(rowHeight, expandedColumnData.length * SUBROW_HEIGHT + 10);
          }
          break;
        case CELL_EXPANSION.CONTEXT:
          {
            // we need to compute the max number of values into a single cell
            const valuesCount: number[] = columns
              .map((column) =>
                traverseAndCollect(
                  params.data,
                  // multiValuesContext column prop provides the custom flexible expansion key, but the path to the data is still the groupField or field
                  column.groupField || column.field,
                  false,
                ),
              )
              .map((valueObjects: any[]) => valueObjects?.length || 0);
            rowHeight = Math.max(rowHeight, Math.max(...valuesCount) * SUBROW_HEIGHT + 10); // 2*5px padding
          }
          break;
      }
    }
  });
  return rowHeight;
};
